import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryConfig } from '@src/lib/api'
import { CreateDealerDTO, Dealer, DealersQueryDTO, UpdateDealerDTO } from '@src/types/dealers'
import { ListResponseDTO, PaginatedResponseDto } from '@src/types/api'
import { dtoToUrlParams } from '@utils/url'
import { Lead, ReplaceLeadCityDTO } from '@src/types/leads'

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: baseQueryConfig('/admin'),
  tagTypes: [ 'DEALER', 'DEALERS' ],
  endpoints: build => ({
    getDealers: build.query<PaginatedResponseDto<Dealer>, DealersQueryDTO>({
      query: (dto) => {
        const { sort, filter, ...params } = dto

        const urlParams = dtoToUrlParams(params)

        if (sort) {
          urlParams.set('sort', JSON.stringify(sort))
        }

        if (filter) {
          urlParams.set('filter', JSON.stringify(filter))
        }

        return { url: `/dealers?${urlParams}` }
      },
      providesTags: (r: PaginatedResponseDto<Dealer> | undefined, error) => ([
        ...(!r || error ? [] : r.result).map(({ id }) => ({ type: 'DEALERS' as const, id })),
        { type: 'DEALERS', id: 'LIST' }
      ])
    }),
    getAllDealers: build.query<ListResponseDTO<Dealer>, void>({
      query: () => ({ url: '/dealers/export' }),
    }),
    createDealer: build.mutation<Dealer, CreateDealerDTO>({
      query: (body) => ({
        method: 'POST',
        url: '/dealers',
        body
      }),
      invalidatesTags: [{ type: 'DEALERS', id: 'LIST' }]
    }),
    updateDealer: build.mutation<Dealer, UpdateDealerDTO>({
      query: ({ id, ...body }) => ({
        method: 'PUT',
        url: `/dealers/${id}`,
        body
      }),
      invalidatesTags: (result, error, dto) => {
        return error || !result ? [] : [{ type: 'DEALERS', id: dto.id }]
      }
    }),
    deleteDealer: build.mutation<Dealer, { id: string }>({
      query: ({ id }) => ({
        method: 'DELETE',
        url: `/dealers/${id}`
      }),
      invalidatesTags: (result, error, { id }) => {
        return error || !result ? [] : [{ type: 'DEALERS', id }]
      }
    }),
    changeDealerState: build.mutation<Dealer, { id: string; state: 'disable' | 'enable' }>({
      query: ({ id, state }) => ({
        method: 'POST',
        url: `/dealers/${id}/${state}`
      }),
      invalidatesTags: (result, error, { id }) => {
        return error || !result ? [] : [{ type: 'DEALERS', id }]
      }
    }),
    changeDealerLeadVerificationNotify: build.mutation<Dealer, { id: string; enabled: boolean }>({
      query: ({ id, ...body }) => ({
        method: 'POST',
        url: `/dealers/${id}/lead-verification-notify`,
        body
      }),
      invalidatesTags: (result, error, { id }) => {
        return error || !result ? [] : [{ type: 'DEALERS', id }]
      }
    }),
    restoreDealerPassword: build.mutation<Dealer, string>({
      query: (id) => ({
        method: 'POST',
        url: `/dealers/${id}/restore-password`
      }),
      invalidatesTags: (result, error, id) => {
        return error || !result ? [] : [{ type: 'DEALERS', id }]
      }
    }),
    changeLeadCheckLimit: build.mutation<Dealer, { id: string; limit: number }>({
      query: ({ id, ...body }) => ({
        method: 'POST',
        url: `/dealers/${id}/lead-check-limit`,
        body
      }),
      invalidatesTags: (result, error, { id }) => {
        return error || !result ? [] : [{ type: 'DEALERS', id }]
      }
    }),
    changeLeadLimit: build.mutation<Dealer, { id: string; limit: number }>({
      query: ({ id, ...body }) => ({
        method: 'POST',
        url: `/dealers/${id}/lead-limit`,
        body
      }),
      invalidatesTags: (result, error, { id }) => {
        return error || !result ? [] : [{ type: 'DEALERS', id }]
      }
    }),
    replaceLeadDealer: build.mutation<Lead, { lead_id: string; dealer_id: string; }>({
      query: ({ lead_id, ...body }) => ({
        method: 'POST',
        url: `/replace-lead-dealer/${lead_id}`,
        body
      }),
      invalidatesTags: (result, error, { dealer_id }) => {
        return error || !result ? [] : [{ type: 'DEALERS', id: dealer_id }]
      }
    }),
    replaceLeadCity: build.mutation<Lead, ReplaceLeadCityDTO>({
      query: ({ lead_id, ...body }) => ({
        method: 'POST',
        url: `/replace-lead-city/${lead_id}`,
        body
      })
    })
  })
})

export const {
  useLazyGetDealersQuery,
  useCreateDealerMutation,
  useUpdateDealerMutation,
  useDeleteDealerMutation,
  useChangeDealerStateMutation,
  useRestoreDealerPasswordMutation,
  useChangeDealerLeadVerificationNotifyMutation,
  useChangeLeadCheckLimitMutation,
  useChangeLeadLimitMutation,
  useReplaceLeadDealerMutation,
  useReplaceLeadCityMutation
} = adminApi